import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Trash2, Loader2, Search, Plus, Building2, Users, Copy, FileText } from "lucide-react";

const DataCleanupCard = React.memo(function DataCleanupCard({
  bulkDeleteDialogOpen,
  setBulkDeleteDialogOpen,
  handleDeleteCompaniesWithNoLeads,
  smartDeletePreview,
  loadingSmartPreview,
  executingSmartDelete,
  handleSmartDeletePreview,
  handleSmartDelete,
  deleteConditions,
  setDeleteConditions,
  deleteCompaniesWithNoLeads,
  setDeleteCompaniesWithNoLeads,
  deletingLeads,
  loadingPreview,
  deletePreviewCount,
  deletePreviewLeads = [],
  handlePreviewDelete,
  handleBulkDelete,
  addCondition,
  removeCondition,
  updateCondition,
  LEAD_FIELDS_FOR_FILTER,
  OPERATORS,
  // Duplicate leads
  duplicateLeadsPreview,
  loadingDuplicateLeadsPreview,
  executingDuplicateLeadsDelete,
  handleDuplicateLeadsPreview,
  handleDuplicateLeadsDelete,
  // Duplicate companies
  duplicateCompaniesPreview,
  loadingDuplicateCompaniesPreview,
  executingDuplicateCompaniesDelete,
  handleDuplicateCompaniesPreview,
  handleDuplicateCompaniesDelete,
  // Duplicate templates
  duplicateTemplatesPreview,
  loadingDuplicateTemplatesPreview,
  executingDuplicateTemplatesDelete,
  handleDuplicateTemplatesPreview,
  handleDuplicateTemplatesDelete,
  // Duplicate forms
  duplicateFormsPreview,
  loadingDuplicateFormsPreview,
  executingDuplicateFormsDelete,
  handleDuplicateFormsPreview,
  handleDuplicateFormsDelete,
}) {
  return (
    <>
      {/* Bulk Delete Leads */}
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            <CardTitle className="text-red-900">Bulk Delete Leads</CardTitle>
          </div>
          <CardDescription className="text-red-700">
            Delete leads based on multiple filter conditions. Companies with no remaining leads will also be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={() => setBulkDeleteDialogOpen(true)}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Open Bulk Delete
          </Button>

          <div className="pt-4 border-t border-red-200">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-red-800">Quick Action: Delete Empty Companies</Label>
            </div>
            <div className="bg-white border border-red-200 rounded-lg p-4 space-y-3">
              <p className="text-sm text-slate-600">
                Delete all companies that have zero leads. This helps clean up your database.
              </p>
              <Button
                onClick={handleDeleteCompaniesWithNoLeads}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                <Building2 className="mr-2 h-4 w-4" />
                Delete All Companies with No Leads
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Bulk Delete */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-orange-900">Smart Bulk Delete</CardTitle>
          </div>
          <CardDescription className="text-orange-700">
            Automatically delete leads with NO valid contact information (no valid email AND no valid phone number). Companies with no remaining leads will also be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-orange-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-orange-900 font-semibold">What will be deleted?</Label>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Leads with NO valid email (null, blank, or invalid format)</li>
                <li>AND NO valid phone number (work phone or mobile phone)</li>
                <li>Companies that have zero leads after deletion</li>
              </ul>
            </div>

            {smartDeletePreview && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Preview Results:</h4>
                <div className="space-y-1 text-sm text-yellow-800 mb-3">
                  <p>⚠️ <strong>{smartDeletePreview.leads_count}</strong> leads will be deleted (no valid contact info)</p>
                  <p>⚠️ <strong>{smartDeletePreview.orphaned_companies_count}</strong> companies will be deleted (no remaining leads)</p>
                </div>
                {smartDeletePreview.sample_leads?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-yellow-900">
                      Sample leads to be deleted (showing {smartDeletePreview.sample_leads.length} of {smartDeletePreview.leads_count}):
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                      {smartDeletePreview.sample_leads.map((lead, i) => (
                        <div key={i} className="bg-white border border-yellow-200 rounded p-2 text-xs">
                          <div className="font-medium text-slate-700">{lead.name}</div>
                          {lead.company && <div className="text-slate-500">Company: {lead.company}</div>}
                          {lead.email && <div className="text-slate-400">Email: {lead.email}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleSmartDeletePreview}
                disabled={loadingSmartPreview}
                variant="outline"
                className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-100"
              >
                {loadingSmartPreview ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Preview Smart Delete
                  </>
                )}
              </Button>

              <Button
                onClick={handleSmartDelete}
                disabled={executingSmartDelete || !smartDeletePreview || smartDeletePreview.leads_count === 0}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {executingSmartDelete ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Execute Smart Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Delete: Duplicate Leads by Work Email */}
      <Card className="border-purple-200 bg-purple-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-purple-900">Deduplicate Leads by Work Email</CardTitle>
          </div>
          <CardDescription className="text-purple-700">
            Find and delete duplicate leads that share the same work email address (case-insensitive). Keeps the oldest entry per email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-purple-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-purple-900 font-semibold">What will be deleted?</Label>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Duplicate leads sharing the same work email address</li>
                <li>The <strong>oldest</strong> lead per email is kept, newer duplicates are removed</li>
                <li>Associated lead notes for deleted duplicates are also removed</li>
                <li>Companies with no remaining leads are cleaned up</li>
              </ul>
            </div>

            {duplicateLeadsPreview && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Preview Results:</h4>
                <div className="space-y-1 text-sm text-yellow-800">
                  <p>⚠️ <strong>{duplicateLeadsPreview.duplicate_groups}</strong> duplicate email groups found</p>
                  <p>⚠️ <strong>{duplicateLeadsPreview.leads_to_delete}</strong> leads will be deleted</p>
                </div>
                {duplicateLeadsPreview.sample_groups?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-yellow-900">
                      Duplicate groups (showing {Math.min(duplicateLeadsPreview.sample_groups.length, 20)} of {duplicateLeadsPreview.duplicate_groups}):
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                      {duplicateLeadsPreview.sample_groups.slice(0, 20).map((g, i) => (
                        <div key={i} className="bg-white border border-yellow-200 rounded p-2 text-xs">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] bg-purple-100 text-purple-700 shrink-0">
                              {g.count} copies
                            </Badge>
                            <span className="font-medium text-purple-800 truncate">{g.email}</span>
                          </div>
                          {g.names?.filter(n => n.trim()).length > 0 && (
                            <div className="text-slate-500 pl-1">
                              Names: {g.names.filter(n => n.trim()).slice(0, 5).join(', ')}
                            </div>
                          )}
                          {g.companies?.length > 0 && (
                            <div className="text-slate-400 pl-1">
                              Companies: {g.companies.slice(0, 3).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleDuplicateLeadsPreview}
                disabled={loadingDuplicateLeadsPreview}
                variant="outline"
                className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                {loadingDuplicateLeadsPreview ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analyzing...</>
                ) : (
                  <><Search className="mr-2 h-4 w-4" />Preview Duplicates</>
                )}
              </Button>
              <Button
                onClick={handleDuplicateLeadsDelete}
                disabled={executingDuplicateLeadsDelete || !duplicateLeadsPreview || duplicateLeadsPreview.leads_to_delete === 0}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {executingDuplicateLeadsDelete ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>
                ) : (
                  <><Trash2 className="mr-2 h-4 w-4" />Delete Duplicates</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Delete: Duplicate Companies by Name */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-blue-900">Deduplicate Companies by Name</CardTitle>
          </div>
          <CardDescription className="text-blue-700">
            Find and merge duplicate companies that share the same name (case-insensitive). Keeps the oldest entry and reassigns leads.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-blue-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-blue-900 font-semibold">What will happen?</Label>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Duplicate companies with the same name are identified</li>
                <li>The <strong>oldest</strong> company per name is kept</li>
                <li>Leads from deleted companies are <strong>reassigned</strong> to the kept company</li>
              </ul>
            </div>

            {duplicateCompaniesPreview && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Preview Results:</h4>
                <div className="space-y-1 text-sm text-yellow-800">
                  <p>⚠️ <strong>{duplicateCompaniesPreview.duplicate_groups}</strong> duplicate company name groups found</p>
                  <p>⚠️ <strong>{duplicateCompaniesPreview.companies_to_delete}</strong> companies will be deleted (leads reassigned)</p>
                </div>
                {duplicateCompaniesPreview.sample_groups?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-medium text-yellow-900">Sample duplicates:</p>
                    {duplicateCompaniesPreview.sample_groups.slice(0, 8).map((g, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-700">
                          {g.count}x
                        </Badge>
                        <span className="truncate font-medium">{g.name}</span>
                        <span className="text-slate-400">({g.lead_counts?.reduce((a, b) => a + b, 0) || 0} leads)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleDuplicateCompaniesPreview}
                disabled={loadingDuplicateCompaniesPreview}
                variant="outline"
                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                {loadingDuplicateCompaniesPreview ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analyzing...</>
                ) : (
                  <><Search className="mr-2 h-4 w-4" />Preview Duplicates</>
                )}
              </Button>
              <Button
                onClick={handleDuplicateCompaniesDelete}
                disabled={executingDuplicateCompaniesDelete || !duplicateCompaniesPreview || duplicateCompaniesPreview.companies_to_delete === 0}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {executingDuplicateCompaniesDelete ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Merging...</>
                ) : (
                  <><Trash2 className="mr-2 h-4 w-4" />Merge &amp; Delete Duplicates</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Delete: Duplicate Templates */}
      <Card className="border-teal-200 bg-teal-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-teal-900">Deduplicate Templates</CardTitle>
          </div>
          <CardDescription className="text-teal-700">
            Find and delete duplicate templates that share the same name and type (case-insensitive). Keeps the oldest entry.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-teal-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-teal-900 font-semibold">What will be deleted?</Label>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Duplicate templates with the same name and type (email, SMS, voicemail)</li>
                <li>The <strong>oldest</strong> template per name+type is kept</li>
              </ul>
            </div>

            {duplicateTemplatesPreview && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Preview Results:</h4>
                <div className="space-y-1 text-sm text-yellow-800">
                  <p>⚠️ <strong>{duplicateTemplatesPreview.duplicate_groups}</strong> duplicate template groups found</p>
                  <p>⚠️ <strong>{duplicateTemplatesPreview.templates_to_delete}</strong> templates will be deleted</p>
                </div>
                {duplicateTemplatesPreview.sample_groups?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-yellow-900">
                      Duplicate groups (showing {Math.min(duplicateTemplatesPreview.sample_groups.length, 50)} of {duplicateTemplatesPreview.duplicate_groups}):
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                      {duplicateTemplatesPreview.sample_groups.slice(0, 50).map((g, i) => (
                        <div key={i} className="bg-white border border-yellow-200 rounded p-2 text-xs">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] bg-teal-100 text-teal-700 shrink-0">
                              {g.count} copies
                            </Badge>
                            <span className="font-medium text-teal-800 truncate">{g.name}</span>
                            <span className="text-slate-400 shrink-0">({g.type})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleDuplicateTemplatesPreview}
                disabled={loadingDuplicateTemplatesPreview}
                variant="outline"
                className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-100"
              >
                {loadingDuplicateTemplatesPreview ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analyzing...</>
                ) : (
                  <><Search className="mr-2 h-4 w-4" />Preview Duplicates</>
                )}
              </Button>
              <Button
                onClick={handleDuplicateTemplatesDelete}
                disabled={executingDuplicateTemplatesDelete || !duplicateTemplatesPreview || duplicateTemplatesPreview.templates_to_delete === 0}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
              >
                {executingDuplicateTemplatesDelete ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>
                ) : (
                  <><Trash2 className="mr-2 h-4 w-4" />Delete Duplicates</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Delete: Duplicate Forms by Name */}
      <Card className="border-pink-200 bg-pink-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-pink-600" />
            <CardTitle className="text-pink-900">Deduplicate Forms by Name</CardTitle>
          </div>
          <CardDescription className="text-pink-700">
            Find and delete duplicate forms that share the same name (case-insensitive). Keeps the oldest entry and reassigns users.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-pink-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-pink-900 font-semibold">What will happen?</Label>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Duplicate forms with the same name are identified</li>
                <li>The <strong>oldest</strong> form per name is kept</li>
                <li>Users assigned to deleted forms are <strong>reassigned</strong> to the kept form</li>
              </ul>
            </div>

            {duplicateFormsPreview && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Preview Results:</h4>
                <div className="space-y-1 text-sm text-yellow-800">
                  <p>&#x26A0;&#xFE0F; <strong>{duplicateFormsPreview.duplicate_groups}</strong> duplicate form name groups found</p>
                  <p>&#x26A0;&#xFE0F; <strong>{duplicateFormsPreview.forms_to_delete}</strong> forms will be deleted</p>
                </div>
                {duplicateFormsPreview.sample_groups?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-yellow-900">
                      Duplicate groups (showing {Math.min(duplicateFormsPreview.sample_groups.length, 50)} of {duplicateFormsPreview.duplicate_groups}):
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                      {duplicateFormsPreview.sample_groups.slice(0, 50).map((g, i) => (
                        <div key={i} className="bg-white border border-yellow-200 rounded p-2 text-xs">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] bg-pink-100 text-pink-700 shrink-0">
                              {g.count} copies
                            </Badge>
                            <span className="font-medium text-pink-800 truncate">
                              {g.display_names?.[0] || g.name}
                            </span>
                          </div>
                          {g.field_counts?.length > 0 && (
                            <div className="text-slate-400 pl-1">
                              Fields per copy: {g.field_counts.slice(0, 5).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleDuplicateFormsPreview}
                disabled={loadingDuplicateFormsPreview}
                variant="outline"
                className="flex-1 border-pink-300 text-pink-700 hover:bg-pink-100"
              >
                {loadingDuplicateFormsPreview ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analyzing...</>
                ) : (
                  <><Search className="mr-2 h-4 w-4" />Preview Duplicates</>
                )}
              </Button>
              <Button
                onClick={handleDuplicateFormsDelete}
                disabled={executingDuplicateFormsDelete || !duplicateFormsPreview || duplicateFormsPreview.forms_to_delete === 0}
                className="flex-1 bg-pink-600 hover:bg-pink-700"
              >
                {executingDuplicateFormsDelete ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>
                ) : (
                  <><Trash2 className="mr-2 h-4 w-4" />Delete Duplicates</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Bulk Delete Leads
            </DialogTitle>
            <DialogDescription>
              Define conditions to filter and delete leads. All conditions must match (AND logic).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Conditions Builder */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Delete Conditions (ALL must match)</Label>
                <Button size="sm" onClick={addCondition} variant="outline">
                  <Plus className="mr-1 h-3 w-3" /> Add Condition
                </Button>
              </div>

              {deleteConditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                  <span className="text-xs text-slate-500 w-8">#{index + 1}</span>

                  {/* Field Selector */}
                  <Select
                    value={condition.field}
                    onValueChange={(value) => updateCondition(index, 'field', value)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_FIELDS_FOR_FILTER.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Operator Selector */}
                  <Select
                    value={condition.operator}
                    onValueChange={(value) => updateCondition(index, 'operator', value)}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPERATORS.map(op => (
                        <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Value Input (only for non-null operators) */}
                  {!['is_null', 'is_not_null'].includes(condition.operator) && (
                    <Input
                      placeholder="Value..."
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      className="flex-1"
                    />
                  )}

                  {/* Remove Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeCondition(index)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Preview Button */}
            <div className="pt-4 border-t">
              <Button
                onClick={handlePreviewDelete}
                disabled={loadingPreview}
                variant="outline"
                className="w-full"
              >
                {loadingPreview ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading Preview...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Preview - Count Matching Leads
                  </>
                )}
              </Button>

              {deletePreviewCount !== null && (
                <div className={`mt-3 rounded-lg border ${deletePreviewCount > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
                  <div className={`p-4 ${deletePreviewCount > 0 ? 'border-b border-yellow-200' : ''}`}>
                    <p className={`text-sm font-semibold ${deletePreviewCount > 0 ? 'text-yellow-900' : 'text-green-900'}`}>
                      {deletePreviewCount > 0
                        ? `⚠️ ${deletePreviewCount} lead${deletePreviewCount !== 1 ? 's' : ''} will be deleted`
                        : '✓ No leads match these conditions'
                      }
                    </p>
                  </div>
                  {deletePreviewLeads.length > 0 && (
                    <div className="p-3 space-y-1">
                      <p className="text-xs font-medium text-yellow-800 mb-2">
                        Showing {deletePreviewLeads.length} of {deletePreviewCount} leads:
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-yellow-100 text-yellow-900">
                              <th className="text-left p-1.5 border border-yellow-200">Name</th>
                              <th className="text-left p-1.5 border border-yellow-200">Email</th>
                              <th className="text-left p-1.5 border border-yellow-200">Company</th>
                              <th className="text-left p-1.5 border border-yellow-200">Status</th>
                              <th className="text-left p-1.5 border border-yellow-200">Owner</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deletePreviewLeads.map((lead, i) => (
                              <tr key={i} className="even:bg-yellow-50/50 hover:bg-yellow-100/50">
                                <td className="p-1.5 border border-yellow-200 font-medium">
                                  {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'}
                                </td>
                                <td className="p-1.5 border border-yellow-200 text-slate-600">{lead.work_email || '—'}</td>
                                <td className="p-1.5 border border-yellow-200 text-slate-600">{lead.company_name || '—'}</td>
                                <td className="p-1.5 border border-yellow-200">
                                  {lead.status ? (
                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{lead.status}</span>
                                  ) : '—'}
                                </td>
                                <td className="p-1.5 border border-yellow-200 text-slate-600">{lead.owner_name || 'Unowned'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Delete Orphaned Companies Option */}
            <div className="flex items-center space-x-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <Checkbox
                id="delete-orphaned-companies"
                checked={deleteCompaniesWithNoLeads}
                onCheckedChange={setDeleteCompaniesWithNoLeads}
              />
              <Label htmlFor="delete-orphaned-companies" className="text-sm text-orange-900 cursor-pointer">
                Also delete companies that have no leads after this deletion
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={deletingLeads || deletePreviewCount === null || deletePreviewCount === 0}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingLeads ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete {deletePreviewCount || 0} Leads
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default DataCleanupCard;
